import { useFocusEffect } from "expo-router";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View, TextInput, Pressable, Text } from "react-native";

import i18n from "@/locales/i18n";

interface InputCryptoAmountProps {
  control?: any;
  name: string;
  value?: string;
  handleChange?: (number: number) => void;
  handleReset?: (boolean: boolean) => void;
  availableBalance: number;
  resetValue: boolean;
  showPercentages?: boolean;
  editable?: boolean;
  placeholder?: string;
  optional?: boolean;
}

export default function InputCryptoAmount(props: InputCryptoAmountProps) {
  const [amount, setAmount] = useState("");
  const [clear, setClear] = useState(false);
  const [separator, setSeparator] = useState(",");
  const detectedLanguage = i18n.language;
  const { t } = useTranslation();

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        setAmount("");
      };
    }, [])
  );

  useEffect(() => {
    if (detectedLanguage && detectedLanguage === "en") {
      setSeparator(".");
    }
  }, [detectedLanguage]);

  useEffect(() => {
    if (props.resetValue) {
      setClear(true);
    }

    if (clear) {
      setAmount("");
      setClear(false);
      props.handleReset(false);
    }
  }, [props.resetValue, clear]);

  function parseMoney(value: string): number {
    value = value.trim();
    value = value.replace(/,/g, ".");
    const parsed = parseFloat(value);

    if (isNaN(parsed)) {
      return;
    }

    return parsed;
  }

  const handleTextChange = (text: string) => {
    let filteredText = text.replace(/[^0-9.,]/g, "");

    if (separator === ",") {
      filteredText = filteredText.replace(/\./g, ",");
    } else {
      filteredText = filteredText.replace(/,/g, ".");
    }

    setAmount(filteredText);
    props.handleChange(parseMoney(filteredText));
  };

  const handleMaxValue = (percentage?: number) => {
    if (percentage) {
      const maxAmount = props.availableBalance * (percentage / 100);
      handleTextChange(maxAmount.toString());
      return;
    }
    handleTextChange(props.availableBalance.toString());
  };

  return (
    <View className="flex-col gap-2 items-center">
      <TextInput
        className="text-center border-b border-solid border-midgray w-80 h-14 text-black1 text-4xl"
        keyboardType="decimal-pad"
        onChangeText={handleTextChange}
        placeholder={props.placeholder}
        placeholderTextColor="#BDBDBD"
        value={amount}
        editable={props.editable}
      />
      <View className={`flex-row gap-3 ${props.showPercentages && "hidden"}`}>
        <Pressable
          className="border-midgray hover:border-purple1 hover:shadow w-fit border rounded-full px-1 mr-2"
          onPress={() => handleMaxValue(25)}
        >
          <Text className="text-sm font-light">
            {t("common:terms.25percent").toUpperCase()}
          </Text>
        </Pressable>
        <Pressable
          className="border-midgray hover:border-purple1 hover:shadow w-fit border rounded-full px-1 mr-2"
          onPress={() => handleMaxValue(50)}
        >
          <Text className="text-sm font-light">
            {t("common:terms.50percent").toUpperCase()}
          </Text>
        </Pressable>
        <Pressable
          className="border-midgray hover:border-purple1 hover:shadow w-fit border rounded-full px-1 mr-2"
          onPress={() => handleMaxValue(75)}
        >
          <Text className="text-sm font-light">
            {t("common:terms.75percent").toUpperCase()}
          </Text>
        </Pressable>
        <Pressable
          className="border-midgray hover:border-purple1 hover:shadow w-fit border rounded-full px-1 mr-2"
          onPress={() => handleMaxValue(100)}
        >
          <Text className="text-sm font-light">
            {t("common:terms.100percent").toUpperCase()}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
